/**
 * Dating Profile Types
 * Complete type definitions for the dating site profile system.
 */

// ==========================================
// CORE PROFILE TYPES
// ==========================================

export interface DatingProfile {
  id: string;
  userId: number;

  // Identity
  displayName: string;
  age: number;
  ageGroup: AgeGroup;
  gender: Gender;
  pronouns?: string;

  // Location
  locationCity?: string;
  locationRegion?: string;
  locationCountry?: string;
  locationLat?: number;
  locationLng?: number;

  // About
  headline?: string;
  bio?: string;

  // Physical
  heightCm?: number;
  bodyType?: BodyType;
  ethnicity?: string;

  // Background
  religion?: string;
  education?: Education;
  occupation?: string;
  languages?: string;

  // Lifestyle
  smoking?: SmokingHabit;
  drinking?: DrinkingHabit;
  diet?: Diet;
  exercise?: ExerciseHabit;
  hasPets?: boolean;
  petTypes?: string;
  hasChildren?: boolean;
  wantsChildren?: WantsChildren;

  // Looking for
  lookingFor: RelationshipType;
  preferredGender?: string;
  ageMin: number;
  ageMax: number;
  distanceMaxKm: number;

  // Gaming
  isGamer: GamerStatus;
  gameLookingForPartners: boolean;

  // V2 Amethyst WU-02: availability windows ("usually here Tue/Thu evenings")
  // and peek-visibility (room-card/peek photo+name exposure). Free-form JSON
  // array on the wire — parse with parseAvailabilityWindows(). peekVisibility
  // is owner-only (omitted/undefined on another user's card).
  availabilityWindowsJson?: string;
  peekVisibility?: PeekVisibility;

  // Status
  isVerified: boolean;
  verificationLevel: VerificationLevel;
  completionPercentage: number;
  lastActiveAt: string;
  isActive: boolean;

  // Collections
  photos: ProfilePhoto[];
  videos: ProfileVideo[];
  interests: UserInterest[];
  prompts: UserPromptAnswer[];

  // Wizard progress (own profile only)
  wizardProgress?: WizardProgress;
}

// ==========================================
// ENUMS & CONSTANTS
// ==========================================

export type Gender = 'man' | 'woman' | 'non-binary' | 'other';
export type AgeGroup = 'young_adult' | 'core_adult' | 'established' | 'mature' | 'senior' | 'elder';
export type BodyType = 'slim' | 'athletic' | 'average' | 'curvy' | 'plus';
export type Education = 'high_school' | 'some_college' | 'bachelors' | 'masters' | 'doctorate' | 'trade_school';
export type SmokingHabit = 'never' | 'sometimes' | 'regularly';
export type DrinkingHabit = 'never' | 'socially' | 'regularly';
export type Diet = 'none' | 'vegetarian' | 'vegan' | 'pescatarian' | 'halal' | 'kosher' | 'other';
export type ExerciseHabit = 'never' | 'sometimes' | 'regularly' | 'daily';
export type WantsChildren = 'yes' | 'no' | 'maybe' | 'open';
export type RelationshipType = 'relationship' | 'casual' | 'friends' | 'marriage' | 'not_sure';
export type GamerStatus = 'yes' | 'sometimes' | 'no';
export type VerificationLevel = 'none' | 'photo' | 'video' | 'id';

// V2 Amethyst WU-02 (R5): who can see a photo-chip + first name on room
// cards / peek sheets before opening the full profile. Default 'initials-only'.
export type PeekVisibility = 'initials-only' | 'matches-only' | 'everyone';

export const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: 'man', label: 'Man' },
  { value: 'woman', label: 'Woman' },
  { value: 'non-binary', label: 'Non-binary' },
  { value: 'other', label: 'Other' },
];

export const BODY_TYPE_OPTIONS: { value: BodyType; label: string }[] = [
  { value: 'slim', label: 'Slim' },
  { value: 'athletic', label: 'Athletic' },
  { value: 'average', label: 'Average' },
  { value: 'curvy', label: 'Curvy' },
  { value: 'plus', label: 'Plus Size' },
];

export const EDUCATION_OPTIONS: { value: Education; label: string }[] = [
  { value: 'high_school', label: 'High School' },
  { value: 'some_college', label: 'Some College' },
  { value: 'trade_school', label: 'Trade School' },
  { value: 'bachelors', label: "Bachelor's Degree" },
  { value: 'masters', label: "Master's Degree" },
  { value: 'doctorate', label: 'Doctorate' },
];

export const SMOKING_OPTIONS: { value: SmokingHabit; label: string }[] = [
  { value: 'never', label: 'Never' },
  { value: 'sometimes', label: 'Sometimes' },
  { value: 'regularly', label: 'Regularly' },
];

export const DRINKING_OPTIONS: { value: DrinkingHabit; label: string }[] = [
  { value: 'never', label: 'Never' },
  { value: 'socially', label: 'Socially' },
  { value: 'regularly', label: 'Regularly' },
];

export const EXERCISE_OPTIONS: { value: ExerciseHabit; label: string }[] = [
  { value: 'never', label: 'Never' },
  { value: 'sometimes', label: 'Sometimes' },
  { value: 'regularly', label: 'Regularly' },
  { value: 'daily', label: 'Daily' },
];

export const WANTS_CHILDREN_OPTIONS: { value: WantsChildren; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'open', label: 'Open to it' },
];

export const RELATIONSHIP_OPTIONS: { value: RelationshipType; label: string }[] = [
  { value: 'relationship', label: 'Long-term relationship' },
  { value: 'casual', label: 'Something casual' },
  { value: 'friends', label: 'New friends' },
  { value: 'marriage', label: 'Marriage' },
  { value: 'not_sure', label: 'Not sure yet' },
];

export const GAMER_STATUS_OPTIONS: { value: GamerStatus; label: string }[] = [
  { value: 'yes', label: 'Yes, gaming is my thing!' },
  { value: 'sometimes', label: 'Sometimes, when I have time' },
  { value: 'no', label: "No, not really into gaming" },
];

export const PEEK_VISIBILITY_OPTIONS: { value: PeekVisibility; label: string; hint: string }[] = [
  { value: 'initials-only', label: 'Initials only', hint: 'Room cards show initials, no photo (default)' },
  { value: 'matches-only', label: 'Matches only', hint: 'People you’ve matched with see your photo + first name' },
  { value: 'everyone', label: 'Everyone', hint: 'Anyone browsing rooms sees your photo + first name' },
];

// ==========================================
// AVAILABILITY WINDOWS (V2 Amethyst WU-02, R2)
// ==========================================
// Free-form JSON array on the wire (no backend schema imposed) — a small,
// frontend-owned shape: which day-of-week x time-of-day slots a person is
// "usually here." Feeds WU-05 matching later.

export type AvailabilityDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type AvailabilityPeriod = 'morning' | 'afternoon' | 'evening';

export interface AvailabilityWindow {
  day: AvailabilityDay;
  period: AvailabilityPeriod;
}

export const AVAILABILITY_DAYS: { value: AvailabilityDay; label: string }[] = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

export const AVAILABILITY_PERIODS: { value: AvailabilityPeriod; label: string }[] = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
];

/** Tolerant parse — malformed/absent JSON degrades to no windows, never throws. */
export function parseAvailabilityWindows(json?: string | null): AvailabilityWindow[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (w): w is AvailabilityWindow =>
        w &&
        typeof w === 'object' &&
        AVAILABILITY_DAYS.some((d) => d.value === w.day) &&
        AVAILABILITY_PERIODS.some((p) => p.value === w.period)
    );
  } catch {
    return [];
  }
}

export function serializeAvailabilityWindows(windows: AvailabilityWindow[]): string {
  return JSON.stringify(windows);
}

/** "Usually here Tue, Thu evenings" — groups shared periods, honest fallback when empty. */
export function formatAvailabilitySummary(windows: AvailabilityWindow[]): string | null {
  if (windows.length === 0) return null;
  const byPeriod = new Map<AvailabilityPeriod, AvailabilityDay[]>();
  for (const w of windows) {
    const days = byPeriod.get(w.period) ?? [];
    days.push(w.day);
    byPeriod.set(w.period, days);
  }
  const dayLabel = (d: AvailabilityDay) => AVAILABILITY_DAYS.find((x) => x.value === d)?.label ?? d;
  const parts = AVAILABILITY_PERIODS.filter((p) => byPeriod.has(p.value)).map((p) => {
    const days = byPeriod.get(p.value)!.map(dayLabel).join('/');
    return `${days} ${p.label.toLowerCase()}s`;
  });
  return `Usually here ${parts.join(', ')}`;
}

export const ETHNICITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'asian', label: 'Asian' },
  { value: 'black', label: 'Black / African Descent' },
  { value: 'hispanic', label: 'Hispanic / Latino' },
  { value: 'middle_eastern', label: 'Middle Eastern' },
  { value: 'native_american', label: 'Native American' },
  { value: 'pacific_islander', label: 'Pacific Islander' },
  { value: 'white', label: 'White / Caucasian' },
  { value: 'mixed', label: 'Mixed / Multi-racial' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

export const RELIGION_OPTIONS: { value: string; label: string }[] = [
  { value: 'agnostic', label: 'Agnostic' },
  { value: 'atheist', label: 'Atheist' },
  { value: 'buddhist', label: 'Buddhist' },
  { value: 'catholic', label: 'Catholic' },
  { value: 'christian', label: 'Christian' },
  { value: 'hindu', label: 'Hindu' },
  { value: 'jewish', label: 'Jewish' },
  { value: 'muslim', label: 'Muslim' },
  { value: 'spiritual', label: 'Spiritual but not religious' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

// ==========================================
// MEDIA TYPES
// ==========================================

export interface ProfilePhoto {
  id: string;
  thumbnailUrl: string;
  mediumUrl: string;
  largeUrl: string;
  isPrimary: boolean;
  isVerified: boolean;
  sortOrder: number;
}

export interface ProfileVideo {
  id: string;
  thumbnailUrl?: string;
  previewUrl?: string;
  videoUrl: string;
  videoType: 'introduction' | 'lifestyle' | 'verification';
  durationSeconds: number;
  isVerified: boolean;
  sortOrder: number;
}

// ==========================================
// INTERESTS
// ==========================================

export interface InterestCategory {
  id: number;
  name: string;
  displayName: string;
  icon?: string;
  interests: Interest[];
}

export interface Interest {
  id: number;
  name: string;
  displayName: string;
  isPopular: boolean;
}

export interface UserInterest {
  id: number;
  interestId: number;
  name: string;
  displayName: string;
  category?: string;
  rank?: number;
}

// ==========================================
// PROMPTS
// ==========================================

export interface PromptTemplate {
  id: number;
  category: PromptCategory;
  promptText: string;
}

export type PromptCategory = 'about_me' | 'interests' | 'dating' | 'values' | 'humor' | 'conversation';

export interface UserPromptAnswer {
  id: string;
  promptId: number;
  promptText: string;
  category: PromptCategory;
  answer: string;
}

// Alias for wizard components
export interface UserPrompt {
  id: string;
  templateId: string;
  answer: string;
  sortOrder: number;
}

export const PROMPT_CATEGORIES: { value: PromptCategory; label: string }[] = [
  { value: 'about_me', label: 'About Me' },
  { value: 'interests', label: 'Interests' },
  { value: 'dating', label: 'Dating' },
  { value: 'values', label: 'Values' },
  { value: 'humor', label: 'Humor' },
  { value: 'conversation', label: 'Conversation Starters' },
];

// ==========================================
// WIZARD PROGRESS
// ==========================================

export interface WizardProgress {
  currentStage: number;
  currentStep: number;
  stage1Completed: boolean;
  stage2Completed: boolean;
  stage3Completed: boolean;
  stage4Completed: boolean;
  stage5Completed: boolean;
  stage6Completed: boolean;
  stage7Completed: boolean;
  allCompleted: boolean;
}

export interface WizardStage {
  id: number;
  title: string;
  description: string;
  isRequired: boolean;
  steps: WizardStep[];
}

export interface WizardStep {
  id: number;
  title: string;
  fields: string[];
}

export const WIZARD_STAGES: WizardStage[] = [
  {
    id: 1,
    title: 'Essentials',
    description: 'Basic info to get you started',
    isRequired: true,
    steps: [
      { id: 1, title: 'Account', fields: ['email', 'password'] },
      { id: 2, title: 'Identity', fields: ['displayName', 'birthdate', 'gender', 'location'] },
      { id: 3, title: 'First Photo', fields: ['photo'] },
    ],
  },
  {
    id: 2,
    title: 'About You',
    description: 'Tell us more about yourself',
    isRequired: false,
    steps: [
      { id: 1, title: 'Physical', fields: ['heightCm', 'bodyType', 'ethnicity'] },
      { id: 2, title: 'Background', fields: ['occupation', 'education', 'religion', 'languages'] },
      { id: 3, title: 'Bio', fields: ['headline', 'bio'] },
    ],
  },
  {
    id: 3,
    title: 'Lifestyle',
    description: 'Your habits and preferences',
    isRequired: false,
    steps: [
      { id: 1, title: 'Habits', fields: ['smoking', 'drinking', 'diet', 'exercise'] },
      { id: 2, title: 'Family', fields: ['hasChildren', 'wantsChildren', 'hasPets', 'petTypes'] },
      { id: 3, title: 'Goals', fields: ['lookingFor', 'preferredGender', 'ageMin', 'ageMax', 'distanceMaxKm'] },
    ],
  },
  {
    id: 4,
    title: 'Interests',
    description: 'What makes you, you',
    isRequired: false,
    steps: [
      { id: 1, title: 'Hobbies', fields: ['interests'] },
      { id: 2, title: 'Entertainment', fields: ['interests'] },
      { id: 3, title: 'Lifestyle', fields: ['interests'] },
    ],
  },
  {
    id: 5,
    title: 'Conversation Starters',
    description: 'Help others start a conversation',
    isRequired: false,
    steps: [
      { id: 1, title: 'Prompts', fields: ['prompts'] },
      { id: 2, title: 'Questions', fields: ['prompts'] },
    ],
  },
  {
    id: 6,
    title: 'More Photos',
    description: 'Add more photos and videos',
    isRequired: false,
    steps: [
      { id: 1, title: 'Photos', fields: ['photos'] },
      { id: 2, title: 'Video', fields: ['video'] },
    ],
  },
  {
    id: 7,
    title: 'Gaming',
    description: 'Optional gaming preferences',
    isRequired: false,
    steps: [
      { id: 1, title: 'Gaming Interest', fields: ['isGamer'] },
      { id: 2, title: 'Character', fields: ['characterClass'] },
      { id: 3, title: 'Preferences', fields: ['gameLookingForPartners'] },
    ],
  },
];

// ==========================================
// API REQUEST/RESPONSE TYPES
// ==========================================

export interface CreateProfileRequest {
  displayName: string;
  birthdate: string;
  gender: Gender;
  pronouns?: string;
  locationCity?: string;
  locationLat?: number;
  locationLng?: number;
  lookingFor: RelationshipType;
  preferredGender?: string;
}

export interface UpdateProfileRequest {
  displayName?: string;
  pronouns?: string;
  locationCity?: string;
  locationRegion?: string;
  locationCountry?: string;
  locationLat?: number;
  locationLng?: number;
  headline?: string;
  bio?: string;
  heightCm?: number;
  bodyType?: BodyType;
  ethnicity?: string;
  religion?: string;
  education?: Education;
  occupation?: string;
  languages?: string;
  smoking?: SmokingHabit;
  drinking?: DrinkingHabit;
  diet?: Diet;
  exercise?: ExerciseHabit;
  hasPets?: boolean;
  petTypes?: string;
  hasChildren?: boolean;
  wantsChildren?: WantsChildren;
  lookingFor?: RelationshipType;
  preferredGender?: string;
  ageMin?: number;
  ageMax?: number;
  distanceMaxKm?: number;
  isGamer?: GamerStatus;
  gameLookingForPartners?: boolean;
  availabilityWindowsJson?: string;
  peekVisibility?: PeekVisibility;
}

export interface WizardStageSubmission {
  step: number;
  profileData?: UpdateProfileRequest;
  interestIds?: number[];
  rankedInterestIds?: number[];
  data?: Record<string, unknown>;
}

export interface CompletionStatus {
  completionPercentage: number;
  wizardProgress: WizardProgress | null;
}
